/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState } from 'react';

import { Column } from 'primereact/column';

import {
  DataTable,
  DataTableFilterMeta,
  DataTableSelectEvent,
  DataTableSelectionSingleChangeEvent
} from 'primereact/datatable';

import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Job } from '../types/processing-schemas.ts';
import { defaultJobsDatableColumns } from './JobsDataTable/JobColumnRender.tsx';
import { TableHeader, Severity, DataTableColumns, TableHeaderProps } from './TableHeader.tsx';
import { FilterProps } from '../types/models-schemas.ts';
import { ActionBodyRender, ActionsProps } from './ActionBodyRender.tsx';
import { useTranslation } from 'react-i18next';

export interface JobsDataTableProps extends FilterProps {
  jobs: Job[];
  className?: string;
  dataTableClassName?: string;
  columns?: DataTableColumns[];
  loading?: boolean;
  selection?: Job;
  onSelectionChange?: (ev: DataTableSelectionSingleChangeEvent<Job[]>) => void;
  onRowUnselected?: (ev: DataTableSelectEvent) => void;
  actions?: Array<ActionsProps<Job>>;
  getCapabilities: TableHeaderProps['getCapabilities'];
  onRefresh?: {
    severity?: Severity;
    command?: () => Promise<unknown>;
  };
}

const defaultFilters = (filterProperties?: Record<string, unknown>): DataTableFilterMeta => {
  return {
    global: { value: filterProperties?.global ?? null, matchMode: FilterMatchMode.CONTAINS },
    processID: {
      operator: FilterOperator.AND,
      constraints: [{ value: filterProperties?.processID ?? null, matchMode: FilterMatchMode.CONTAINS }]
    },
    project_ref: {
      operator: FilterOperator.AND,
      constraints: [{ value: filterProperties?.project_ref ?? null, matchMode: FilterMatchMode.CONTAINS }]
    },
    status: {
      operator: FilterOperator.OR,
      constraints: [{ value: filterProperties?.status ?? null, matchMode: FilterMatchMode.EQUALS }]
    }
  };
};

export const JobsDataTable: React.FC<JobsDataTableProps> = (props) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters());

  const [globalFilterValue, setGlobalFilterValue] = useState<string>();

  const initFilters = (_defaultFilters?: Record<string, unknown>) => {
    const _filters = defaultFilters(_defaultFilters);
    setFilters(_filters);
    setGlobalFilterValue(_filters.global['value']);
  };

  useEffect(() => {
    initFilters(props.defaultFilters);
  }, [props.defaultFilters]);

  const getMapColumns = () => {
    return props.columns ?? defaultJobsDatableColumns;
  };

  const clearFilters = (): void => {
    initFilters();
    if (props.onClearFilters) {
      props.onClearFilters.command?.(defaultFilters());
    }
  };

  return (
    <div className={`${props.className ?? ''} w-full mt-2`}>
      <DataTable
        header={
          <TableHeader
            onRefresh={props.onRefresh}
            onClearFilters={{ severity: props.onClearFilters?.severity, command: clearFilters }}
            filters={filters}
            setFilters={(filters) => {
              setFilters(filters);
              props.handleGlobalFilters?.(filters);
            }}
            globalFilterValue={globalFilterValue}
            setGlobalFilterValue={setGlobalFilterValue}
            getCapabilities={props.getCapabilities}
          />
        }
        sortField={'created'}
        sortMode={'single'}
        sortOrder={-1}
        dataKey="jobID"
        filters={filters}
        emptyMessage={t(['misc.404', 'No available data'])}
        filterDisplay="menu"
        globalFilterFields={['processID', 'jobID', 'status', 'project_ref'] as Array<keyof Job>}
        onFilter={({ filters }) => {
          setFilters(filters);
          props.handlePropertiesFilters?.(filters);
        }}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50]}
        onSelectionChange={props.onSelectionChange}
        onRowSelect={props.onRowUnselected}
        selection={props.selection}
        selectionMode={'single'}
        stripedRows
        loading={props.loading}
        size={'small'}
        value={props.jobs}
        className={props.dataTableClassName}
        tableStyle={{ width: '100%' }}
      >
        {getMapColumns()
          .toSorted((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY))
          .map((c) => (
            <Column className="cursor-auto" key={c.field} {...c} />
          ))}
        {props.actions && props.actions.length > 0 && (
          <Column
            header="Actions"
            align="center"
            body={(item) => <ActionBodyRender<Job> item={item} actions={props.actions} />}
          />
        )}
      </DataTable>
    </div>
  );
};
